import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, AlertTriangle, CheckCircle, Clock, User, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendSignInEmail } from "@/utils/emailService";

interface AccessAttempt {
  id: string;
  user_id: string | null;
  attempted_at: string;
  access_granted: boolean;
  page_attempted: string;
  user_agent: string | null;
  ip_address: string | null;
}

export const AccessAttemptLogs = () => {
  const [attempts, setAttempts] = useState<AccessAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    unauthorized: 0,
    authorized: 0,
    today: 0
  });
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadAccessAttempts();
  }, []);

  const loadAccessAttempts = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('admin_access_attempts')
        .select('*')
        .order('attempted_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const attempts = data || [];
      setAttempts(attempts);

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayAttempts = attempts.filter(attempt => 
        new Date(attempt.attempted_at) >= today
      );

      setStats({
        total: attempts.length,
        unauthorized: attempts.filter(a => !a.access_granted).length,
        authorized: attempts.filter(a => a.access_granted).length,
        today: todayAttempts.length
      });

    } catch (error) {
      console.error('Error loading access attempts:', error);
      toast.error('Failed to load access attempts');
    } finally {
      setLoading(false);
    }
  };

  const loadUserEmail = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setTestEmail(user?.email ?? '');
  };

  useEffect(() => {
    loadUserEmail();
  }, []);

  const handleSendTestEmail = async () => {
    try {
      if (!testEmail) {
        toast.error('Please provide an email address');
        return;
      }
      setSending(true);
      await sendSignInEmail(testEmail, window.location.origin + '/');
      toast.success('Test email sent via Resend');
    } catch (error) {
      console.error('Test email failed:', error);
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (granted: boolean) => {
    return granted ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-red-600" />
    );
  };

  const getStatusBadge = (granted: boolean) => {
    return granted ? (
      <Badge variant="secondary" className="bg-green-100 text-green-800">
        Authorized
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-red-100 text-red-800">
        Unauthorized
      </Badge>
    );
  };

  const formatUserAgent = (userAgent: string | null) => {
    if (!userAgent) return 'Unknown';
    
    // Extract browser info
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    
    return 'Other';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-pulse">
            <Shield className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-muted-foreground">Loading access attempts...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Resend Email Test</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <Input
            placeholder="Email address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="h-9 max-w-sm"
          />
          <Button onClick={handleSendTestEmail} disabled={sending} size="sm">
            {sending ? 'Sending...' : 'Send test email'}
          </Button>
        </CardContent>
      </Card>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <Shield className="h-5 w-5 mx-auto mb-1 text-blue-600" />
            <div className="text-lg font-bold">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total Attempts</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 text-center">
            <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-600" />
            <div className="text-lg font-bold">{stats.unauthorized}</div>
            <div className="text-xs text-muted-foreground">Unauthorized</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 text-center">
            <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-600" />
            <div className="text-lg font-bold">{stats.authorized}</div>
            <div className="text-xs text-muted-foreground">Authorized</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 text-center">
            <Clock className="h-5 w-5 mx-auto mb-1 text-purple-600" />
            <div className="text-lg font-bold">{stats.today}</div>
            <div className="text-xs text-muted-foreground">Today</div>
          </CardContent>
        </Card>
      </div>

      {/* Access Attempts Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Shield className="h-4 w-4" />
            Recent Access Attempts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {attempts.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-muted-foreground">No access attempts recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(attempt.access_granted)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-3 w-3 text-gray-500" />
                        <span className="font-medium">
                          {attempt.user_id ? `User ${attempt.user_id.slice(0, 8)}...` : 'Anonymous'}
                        </span>
                        {getStatusBadge(attempt.access_granted)}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(attempt.attempted_at).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {formatUserAgent(attempt.user_agent)}
                        </span>
                        <span>Page: {attempt.page_attempted}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};